import {
  Button,
  Card,
  CircularLoader,
  Drawer,
  H4,
  Icon,
  Stack,
  Table,
  Tag,
  Title,
  Tooltip,
  useActions
} from "@introist/react-foundation/v2";
import { DrawerHeader } from "../../../foundation/templates/Drawer/DrawerHeader";
import { DataSourceIcon } from "../../datasources/components/DataSourceIcon";
import { useDatasetSyncConfig, useDatasetSyncActions } from "./useDatasetSyncConfig";
import { api } from "../../../services/rpc/RpcProvider";
import { useEffect, useMemo } from "react";
import { useDatasetSyncLogs } from "./useDatasetSyncLogs";
import { useNavigate } from "react-router";
import { SyncMapping } from "./SyncMapping";
import { formatDateTime } from "../../../utils/DatesUtils";
import { DateFormats } from "../../../utils/dates";
import { ScheduleManager } from "./ScheduleEditor";

export const DatasetSyncDrawer = ({
  datasetId,
  open,
  onClose
}: {
  datasetId: string;
  open: boolean;
  onClose: () => unknown;
}) => {
  const navigate = useNavigate();
  const { onConfirmAction } = useActions();

  const { config } = useDatasetSyncConfig(datasetId);

  const { update, remove } = useDatasetSyncActions();

  const { data: ds } = api.employees.dataSources.find.useQuery(
    { dataSourceId: config?.dataSourceId ?? "" },
    { enabled: !!config }
  );

  const { logs } = useDatasetSyncLogs(datasetId);

  const onDelete = onConfirmAction(
    async () => {
      await remove(config!.id).then(() => navigate("..", { relative: "path" }));
    },
    {
      title: "Delete sync",
      description: "Are you sure you want to delete this sync?",
      confirmTitle: "Delete",
      icon: "trash"
    }
  );

  const render = () => {
    if (!config || !ds) return <CircularLoader fillParent />;

    return (
      <Stack vertical style={{ marginTop: "var(--spacing-xLarge)" }}>
        <Card>
          <Stack>
            <Icon name={config.syncEnabled ? "playCircle" : "pause"} />
            <Button variant="blended">
              <Title>{`This sync is ${config.syncEnabled ? "active" : "paused"}.`}</Title>
            </Button>
            {config.syncEnabled ? (
              <Button
                variant="blended"
                size="small"
                style={{ marginLeft: "auto" }}
                onClickWithLoading={() => {
                  return update(config.id, { syncEnabled: false });
                }}
              >
                Pause
              </Button>
            ) : (
              <Button
                size="small"
                variant="outlined"
                style={{ marginLeft: "auto" }}
                onClickWithLoading={() => {
                  return update(config.id, { syncEnabled: true });
                }}
              >
                Activate
              </Button>
            )}
          </Stack>
        </Card>
        <Card>
          <Stack vertical>
            <Stack>
              <Icon name="time" />
              <ScheduleManager
                initialSchedule={{
                  days: config.syncDays,
                  hours: config.syncHours,
                  timezone: config.timezone
                }}
                onSave={schedule =>
                  update(config.id, {
                    syncDays: schedule.days,
                    syncHours: schedule.hours,
                    timezone: schedule.timezone
                  })
                }
              />
              <RunSyncButton datasetId={datasetId} />
            </Stack>
          </Stack>
        </Card>
        <SyncMapping datasetId={datasetId} />
        <H4>Recent syncs</H4>
        {logs && logs.length === 0 && <Title>Nothing has been synced yet</Title>}
        {logs && logs.length > 0 && (
          <Table
            columns={[
              {
                key: "startedAt",
                title: "Started at",
                render: log => (
                  <Title>{formatDateTime(log.startedAt, DateFormats.datetime.long)}</Title>
                )
              },
              {
                key: "status",
                title: "Status",
                render: log => <StatusTag status={log.status} details={log.details} />
              },

              {
                key: "created",
                title: "Created",
                render: log => <Title>{log.recordCounts?.created}</Title>,
                textAlign: "center",
                width: "30px"
              },
              {
                key: "updated",
                title: "Updated",
                render: log => <Title>{log.recordCounts?.updated}</Title>,
                textAlign: "center",
                width: "30px"
              },
              {
                key: "failed",
                title: "Failed",
                render: log => <Title>{log.recordCounts?.failed}</Title>,
                textAlign: "center",
                width: "30px"
              }
            ]}
            data={logs}
          />
        )}
      </Stack>
    );
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width="480px"
      style={{ padding: "var(--spacing-xLarge)" }}
    >
      <DrawerHeader
        startAdornment={ds ? <DataSourceIcon dataSource={ds.sourceType} /> : null}
        title={ds && config ? `${ds.name} ${config.objectType}` : ""}
        actions={[
          { key: "delete", title: "Delete sync", startAdornmentIcon: "trash", onClick: onDelete }
        ]}
        onClose={onClose}
      />
      {render()}
    </Drawer>
  );
};

const StatusTag = ({ status, details }: { status: string; details?: string | null }) => {
  let color: "success" | "danger" | "outlined" | "surface";

  switch (status) {
    case "success":
      color = "success";
      break;
    case "failed":
      color = "danger";
      break;
    case "running":
      color = "outlined";
      break;
    default:
      color = "surface";
  }

  const tag = <Tag colorVariant={color}>{status}</Tag>;

  if (!details) return tag;
  return <Tooltip tooltip={details}>{tag}</Tooltip>;
};

const RunSyncButton = ({ datasetId }: { datasetId: string }) => {
  const { logs, refetch } = useDatasetSyncLogs(datasetId);

  const { start } = useDatasetSyncActions();

  useEffect(() => {
    const interval = setInterval(() => {
      refetch();
    }, 10000);

    return () => clearInterval(interval);
  }, [refetch]);

  const isRunning = useMemo(() => {
    if (!logs) return false;
    if (logs.length === 0) return false;
    return logs[0].status === "running";
  }, [logs]);

  if (!logs) return null;

  return (
    <Button
      variant="blended"
      size="small"
      style={{ marginLeft: "auto" }}
      onClickWithLoading={() => start(datasetId)}
      disabled={isRunning}
      startAdornment={isRunning ? <CircularLoader size="small" /> : null}
    >
      {isRunning ? "Sync in progress" : "Run now"}
    </Button>
  );
};
